import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  dismissAssistantHint,
  fetchAssistantPulse,
  fetchAssistantUserPreferences,
  updateAssistantUserPreferences,
} from '@/api/assistantProactive/assistantProactiveApi';
import type {
  AssistantHintKey,
  AssistantPulse,
  AssistantUserPreferences,
  AssistantUserPreferencesUpdate,
} from '@/api/assistantProactive/assistantProactiveApi.types';
import { LIVE_STATUS_REFETCH_INTERVAL_MILLISECONDS } from '@/constants/liveStatus';
import { ASSISTANT_PREFERENCES_QUERY_KEY, ASSISTANT_PULSE_QUERY_KEY } from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { useIsAssistantAvailable } from '@/hooks/useIsAssistantAvailable';
import { readCurrentLocalMoment } from '@/utils/localMoment';

export function useAssistantPulseQuery(): UseQueryResult<AssistantPulse> {
  const isAvailable = useIsAssistantAvailable();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: [...ASSISTANT_PULSE_QUERY_KEY, activeTenantId],
    queryFn: function fetchPulseForNow() {
      return fetchAssistantPulse(readCurrentLocalMoment());
    },
    enabled: isAvailable,
    refetchInterval: LIVE_STATUS_REFETCH_INTERVAL_MILLISECONDS,
    refetchOnWindowFocus: true,
  });
}

export function useAssistantUserPreferencesQuery(): UseQueryResult<AssistantUserPreferences> {
  const isAvailable = useIsAssistantAvailable();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: [...ASSISTANT_PREFERENCES_QUERY_KEY, activeTenantId],
    queryFn: fetchAssistantUserPreferences,
    enabled: isAvailable,
  });
}

export function useUpdateAssistantUserPreferencesMutation(): UseMutationResult<
  AssistantUserPreferences,
  unknown,
  AssistantUserPreferencesUpdate
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAssistantUserPreferences,
    onSuccess: function refreshProactiveState() {
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_PREFERENCES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_PULSE_QUERY_KEY });
    },
  });
}

export function useDismissAssistantHintMutation(): UseMutationResult<
  void,
  unknown,
  AssistantHintKey
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissAssistantHint,
    onSuccess: function refreshPulse() {
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_PULSE_QUERY_KEY });
    },
  });
}
